import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from '@/lib/utils';

const TooltipProvider = TooltipPrimitive.Provider;

/**
 * Touch-friendly tooltip implementation.
 * Detects touch/coarse-pointer devices via matchMedia and enables tap-to-toggle.
 * On desktop (fine pointer), works normally via hover.
 */

function isTouchDevice() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(pointer: coarse)').matches;
}

const TouchContext = React.createContext(null);

function Tooltip({ children, ...props }) {
  const [open, setOpen] = React.useState(false);
  const isTouch = React.useMemo(() => isTouchDevice(), []);

  const toggle = React.useCallback(() => setOpen((prev) => !prev), []);

  const handleOpenChange = (nextOpen) => {
    if (isTouch) {
      // On touch: only close via escape/outside-click (nextOpen=false from Radix)
      if (!nextOpen) setOpen(false);
    } else {
      setOpen(nextOpen);
    }
  };

  const contextValue = React.useMemo(() => ({ isTouch, toggle }), [isTouch, toggle]);

  return (
    <TouchContext.Provider value={contextValue}>
      <TooltipPrimitive.Root open={open} onOpenChange={handleOpenChange} {...props}>
        {children}
      </TooltipPrimitive.Root>
    </TouchContext.Provider>
  );
}

const TooltipTrigger = React.forwardRef(({ onClick, ...props }, ref) => {
  const ctx = React.useContext(TouchContext);

  const handleClick = (e) => {
    if (ctx?.isTouch) {
      ctx.toggle();
    }
    onClick?.(e);
  };

  return <TooltipPrimitive.Trigger ref={ref} onClick={handleClick} {...props} />;
});
TooltipTrigger.displayName = 'TooltipTrigger';

const TooltipContent = React.forwardRef(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-50 overflow-hidden rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-tooltip-content-transform-origin]',
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
